import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { EventProvider, useEvent } from './contexts/EventContext';
import { Toaster } from './components/ui/sonner';
import LandingPage from './pages/LandingPage';
import Gallery from './pages/Gallery';
import Leaderboard from './pages/Leaderboard';
import CarDetail from './pages/CarDetail';
import PublicVoting from './pages/PublicVoting';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import JudgeDashboard from './pages/JudgeDashboard';
import PrintDashCards from './pages/PrintDashCards';
import './App.css';
import { installSessionKeeper } from './lib/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API };

// Install global axios session keeper (auto-refresh on 401, sliding refresh
// while tab is open). Safe to call multiple times — idempotent.
installSessionKeeper();

// Theme Applier Component
function ThemeApplier({ children }) {
  const { currentEvent } = useEvent();

  useEffect(() => {
    if (currentEvent && currentEvent.theme) {
      const theme = currentEvent.theme;
      const root = document.documentElement;

      // Apply CSS variables
      if (theme.primary_color) root.style.setProperty('--color-primary', theme.primary_color);
      if (theme.secondary_color) root.style.setProperty('--color-secondary', theme.secondary_color);
      if (theme.accent_color) root.style.setProperty('--color-accent', theme.accent_color);
      if (theme.text_color) root.style.setProperty('--color-text', theme.text_color);
      if (theme.text_secondary_color) root.style.setProperty('--color-text-secondary', theme.text_secondary_color);
      if (theme.heading_color) root.style.setProperty('--color-heading', theme.heading_color);
      
      if (theme.background_color) {
        root.style.setProperty('--color-background', theme.background_color);
        document.body.style.background = theme.background_color;
      }
      
      // Calculate shadow color from accent
      if (theme.accent_color) {
        const hex = theme.accent_color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        root.style.setProperty('--shadow-accent', `rgba(${r}, ${g}, ${b}, 0.3)`);
      }

      // Apply font family
      if (theme.font_family) {
        const link = document.createElement('link');
        link.href = `https://fonts.googleapis.com/css2?family=${theme.font_family.replace(' ', '+')}:wght@300;400;500;600;700&display=swap`;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        
        root.style.setProperty('--font-family', theme.font_family);
        document.body.style.fontFamily = theme.font_family;
      }
    }
  }, [currentEvent]);

  return children;
}

// Protected Route Component
function ProtectedRoute({ children, requiredRole }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(response => {
      setUser(response.data);
      setLoading(false);
    }).catch(() => {
      localStorage.removeItem('token');
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
    </div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user.role !== requiredRole && user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  return (
    <EventProvider>
      <ThemeApplier>
        <div className="App">
          <Toaster position="top-center" richColors />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/car/:carId" element={<CarDetail />} />
              <Route path="/vote" element={<PublicVoting />} />
              <Route path="/login" element={<Login />} />
              <Route path="/judge/dashboard" element={
                <ProtectedRoute requiredRole="judge">
                  <JudgeDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/dashboard" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/print-dash-cards" element={
                <ProtectedRoute requiredRole="admin">
                  <PrintDashCards />
                </ProtectedRoute>
              } />
              {/* Shorthand route for admin */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </ThemeApplier>
    </EventProvider>
  );
}

export default App;
